import { Injectable, ElementRef } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Media, Playback } from '../playback';
import { Logger, Shell } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { AvAsset } from '@tytapp/models';

export interface Playable {
    id? : string;
    title? : string;
    description? : string;
    readonly asset : AvAsset;
    adapt(data : any);
}

export interface Playlist extends Playable {
    playables : Playable[];
}

export interface QueueItem {
    parent? : QueueItem;
    id : string;
    media : Media;
    playable? : Playable;
    finished : boolean;

    /**
     * Whether the player should resume this item from the position given in `position`
     */
    resume : boolean;

    /**
     * True if the queue item was added "incidentally", that means that the 
     * user didn't explicitly ask to be shown this later.
     */
    incidental : boolean;

    /**
     * The position the user reached while playing this content
     */
    position? : number;

    /**
     * Subitems which come from Playlist-type playables such as multi-part episodes.
     */
    subitems : QueueItem[];
}

interface QueueAdvancementResult {
    finished? : QueueItem;
    next : QueueItem;
}

/**
 * The player service which controls the primary player UI 
 */
@Injectable()
export class PlayerService {
    constructor(
        private shell : Shell,
        private playback : Playback,
        private userService : UserService
    ) {

    }

    private _history : QueueItem[] = [];
    private _queue : QueueItem[] = [];
    private _currentQueueItem : QueueItem = null;
    private _nextQueueItem : string = null;
    private _media : Media;
    
    /**
     * Play a piece of media immediately. Parameter can be a URL or a Playable.
     */
    play(media : string | Playable) {
        let queueItem = this.createQueueItem(media, true);

        if (!this.isSupported(queueItem)) {
            this.shell.showDialog('message', 'Error', `No available media plugin can handle media with URL ${queueItem.media.url}`);
            return false;
        }

        this._currentQueueItem = queueItem;
        this.addToHeadOfQueue(queueItem);
        this.startPlaying(queueItem);
    }

    isSupported(queueItem : QueueItem) {
        if (queueItem.playable && queueItem.playable['playables']) {
            return true;
        }
    
        // Resolve the media, if necessary.

        let resolution = queueItem.media.resolution;
        
        if (!resolution) {
            queueItem.media.resolution 
                = resolution
                = this.playback.resolve(<any>queueItem.media); // TODO
        }

        // If no resolution was found, then the URL is not supported. This is either a bug 
        // or a human error.

        return !!resolution;
    }

    playQueueItem(item : QueueItem) {
        this.startPlaying(item);
    }

    removeQueueItem(item : QueueItem) {
        if (item.parent) {
            item.parent.subitems = item.parent.subitems.filter(x => x !== item);
        } else {
            this._queue = this._queue.filter(x => x !== item);
        }
        this.triggerQueueChanged();
    }

    skipToQueueItem(item : QueueItem) {
        while (this._queue.length > 0) {
            let nextResult = this.getNextQueueItemAfter(this._currentQueueItem);
            if (nextResult.finished) {
                this._queue = this._queue.filter(x => x !== nextResult.finished);
                this.triggerQueueChanged();
            }

            if (nextResult.next === item) {
                this.startPlaying(item);
                return true;
            }
        }

        return false;
    }

    /**
     * Play a piece of media immediately. Parameter can be a URL or a Playable.
     */
    playNow(media : string | Playable) {

        let queueItem = this.createQueueItem(media, true);

        if (!this.isSupported(queueItem)) {
            this.shell.showDialog('message', 'Error', `No available media plugin can handle media with URL ${queueItem.media.url}`);
            return false;
        }

        if (this._currentQueueItem) {
            // We are already playing something, so add that item back to the head of the queue 
            // and start playing the requested media, so that the current media resumes after this is completed.
            this._currentQueueItem.resume = true;

            //this.addToHeadOfQueue(this._currentQueueItem);
        }

        this._currentQueueItem = queueItem;
        this.addToHeadOfQueue(queueItem);
        this.startPlaying(queueItem);
    }

    /**
     * Play a piece of media now or later if something is already playing. Parameter can be a URL or a Playable
     * @param media 
     */
    playNext(media : string | Playable): boolean {
        if (this._currentQueueItem) {
            return this.addNextInQueue(this.createQueueItem(media, false));
        } else {
            return this.play(media);
        }
    }

    /**
     * Play a piece of media now or later if something is already playing. Parameter can be a URL or a Playable
     * @param media 
     */
    playLater(media : string | Playable) {
        if (this._currentQueueItem) {
            this.addToEndOfQueue(this.createQueueItem(media, false));
        } else {
            this.play(media);
        }
    }

    /**
     * Start playing the given queue item immediately.
     * @param queueItem 
     */
    private startPlaying(queueItem : QueueItem) {
        if (queueItem.subitems.length > 0) {
            this.startPlaying(queueItem.subitems[0]);
            return;
        }

        if (queueItem.finished) {
            // This queue item was already watched. Skip to the next one.
            let nextItem = this.getNextQueueItemAfter(queueItem);
            if (nextItem.finished) {
                this._queue = this._queue.filter(x => x !== nextItem.finished);
                this.triggerQueueChanged();

                if (nextItem.next) {
                    this.startPlaying(nextItem.next);
                }
                return false;
            }
        }

        this._currentQueueItem = queueItem;

        if (queueItem.resume && queueItem.position) {
            this.resumePosition = queueItem.position;
        } else {
            this.resumePosition = 0;
        }

        this.media = queueItem.media;
        return true;
    }

    /**
     * The position which the player should start at when playing new media.
     */
    public resumePosition : number;

    /**
     * Instruct the player to stop playing the current media and close itself.
     */
    public stopPlaying() {
        this.media = null;
        if (this._currentQueueItem) {
            this.removeQueueItem(this._currentQueueItem);
            this._currentQueueItem = null;
        }

    }

    /**
     * Generate a new queue ID (uuid v4)
     */
    private generateQueueId() {
        var result, i, j;
        result = '';
        for(j=0; j<32; j++) {
            if( j == 8 || j == 12|| j == 16|| j == 20) 
            result = result + '-';
            i = Math.floor(Math.random()*16).toString(16).toUpperCase();
            result = result + i;
        }
        return result;
    }

    private createQueueItem(mediaOrUrl : string | Playable, incidental : boolean, parent : QueueItem = null) : QueueItem {
        if (typeof mediaOrUrl === 'object') {
            let playable : Playable = mediaOrUrl;
            let subitems = [];

            // Create the item

            let item = {
                parent,
                id: this.generateQueueId(),
                media: new Media(playable.asset.url),
                playable,
                resume: false,
                subitems,
                incidental,
                finished: false
            };

            // If this playable is a playlist, we need to resolve the items into the queue graph.

            if (playable['playables']) {
                let playlist : Playlist = <Playlist>playable;
                item.subitems = playlist.playables.map(x => this.createQueueItem(x, incidental, item));
            }

            return item;

        } else if (typeof mediaOrUrl === 'string') {

            let media = new Media(mediaOrUrl);

            return {
                id: this.generateQueueId(),
                media, 
                playable: null,
                resume: false,
                parent: null,
                subitems: [],
                incidental,
                finished: false
            };

        }

        // This value is not playable

        Logger.inspect('Invalid media passed to PlayerService.play()', mediaOrUrl);
        throw new Error('Cannot play back object of type '+(typeof mediaOrUrl));
    }

    private appendToHistory(item : QueueItem) {
        this._history.push(item);
    }

    public updatePlaybackPosition(position : number) {
        if (!this._currentQueueItem)
            return;
        
        this._currentQueueItem.position = position;
    }

    public hasMoreMediaQueued() {
        if (!this._currentQueueItem)
            return this._queue.length > 0;
        
        let result = this.getNextQueueItemAfter(this._currentQueueItem);
        return !!result.next;
    }

    public finishedPlaying() {
        // The player has finished playing back the current item.
        // Record the previous item into history

        let nextItem : QueueItem;

        // Dequeue the next queue item if we have one

        if (this._currentQueueItem) {
            this._currentQueueItem.finished = true;
            let result = this.getNextQueueItemAfter(this._currentQueueItem);

            if (result.finished) {
                result.finished.finished = true;
                this._queue = this._queue.filter(x => x !== result.finished);
                this.appendToHistory(result.finished);
                this.triggerQueueChanged();
            }
            
            nextItem = result.next;
        }
        
        // If we have a next item, play it

        if (nextItem) {
            this._currentQueueItem = nextItem;
            this.startPlaying(nextItem);
        } else {
            this._currentQueueItem = null;
            this.stopPlaying();
        }
    }

    private getNextQueueItemAfter(item : QueueItem) : QueueAdvancementResult {

        if (!item)
            throw new Error('No queue item passed');

        // Attempt to walk to the next item on any level of the queue

        let traversedItem = item;
        while (traversedItem.parent != null) {
            let parent = traversedItem.parent;
            let index = parent.subitems.indexOf(traversedItem);
            if (index + 1 >= parent.subitems.length) {
                traversedItem = parent;
            } else {
                return {
                    next: parent.subitems[index + 1]
                };
            }
        }

        // We traversed the entire graph wrt `item` and now must release the traversed 
        // item from the queue, moving on to the next item if possible

        let result : QueueAdvancementResult = {
            finished: traversedItem,
            next: null
        }

        let index = this._queue.indexOf(traversedItem);
        if (index + 1 < this._queue.length) {
            result.next = this._queue[index + 1];
        }

        return result;
    }

    private getQueueItemById(id : string, set : QueueItem[] = null): QueueItem {
        if (!set)
            set = this._queue;
        
        for (let item of set) {
            if (item.id == id)
                return item;
            
            let subitem = this.getQueueItemById(id, item.subitems);

            if (subitem)
                return subitem;
        }

        return null;
    }

    private dequeueItem() : QueueItem {
        let item = this._queue[0];

        return item;
    }

    private addNextInQueue(item : QueueItem) {

        if (!this.isSupported(item)) {
            this.shell.showDialog('message', 'Error', `No available media plugin can handle media with URL ${item.media.url}`);
            return false;
        }

        if (this._queue.length == 0) {
            this._queue = [item];
            return;
        }

        this._queue = [this._queue[0], item].concat(this._queue.slice(1));
        this.triggerQueueChanged();
    }

    /**
     * Trigger the queueChanged event. 
     * Event is triggered on next VM turn to prevent duplicate events as well as to ensure 
     * that the subscribers to this event have the full state of the player updated.
     */
    private triggerQueueChanged() {
        clearTimeout(this._queueChangeTrigger);
        this._queueChangeTrigger = setTimeout(() => {
            this._queueChanged.next();
        });
    }

    private _queueChangeTrigger = null;


    private _queueChanged : Subject<void> = new Subject<void>();

    public get queueChanged() {
        return this._queueChanged;
    }

    private addToHeadOfQueue(item : QueueItem) {
        this._queue.unshift(item);
        this.triggerQueueChanged();
    }

    private addToEndOfQueue(item : QueueItem) {
        this._queue.push(item);
        this.triggerQueueChanged();
    }

    public clearQueue() {
        this._queue = [];

        // The currently playing media will no longer have any relation to other graph items
        if (this._currentQueueItem)
            this._currentQueueItem.parent = null;
        
        this.triggerQueueChanged();
    }

    get queue() : QueueItem[] {
        return this._queue.slice(0);
    }

    get history() : QueueItem[] {
        return this._history.slice(0);
    }

    get media() : Media {
        return this._media;
    }

    set media(media : Media) {
        this._media = media;
        this._mediaChange.next(this._media);
    }

    private _mediaChange : Subject<Media> = new Subject<Media>();

    get currentItem() : QueueItem {
        return this._currentQueueItem;
    }

    get mediaChange() : Observable<Media> {
        return this._mediaChange;
    }
}